import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ScreenBackdrop } from './ScreenBackdrop';
import { Confetti } from './Confetti';
import { ChevronRight } from './icons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { useTelegram } from '../../hooks/useTelegram';
import { redeemPromocode, promocodeErrorMessage, type RedeemResult } from '../../lib/promocode';
import { UsdtIcon } from '../icons/UsdtIcon';
import coin from '../../assets/brand/incpt-coin.png';

/** Paste a code, get INCPT or USDT in Liquid staking. A code works exactly once. */
export function PromocodeScreen({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const { impact, notify } = useHaptic();
  const { initData } = useTelegram();
  const qc = useQueryClient();
  useBackButton(onBack);
  const tgBack = hasTelegramBackButton();

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RedeemResult | null>(null);

  const usdt = done?.rewardKind === 'liquid_usdt';
  const clean = code.trim().toUpperCase();
  const canSubmit = clean.length >= 4 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    impact('medium');
    setBusy(true);
    setError(null);
    try {
      const result = await redeemPromocode(initData, clean);
      notify('success');
      setDone(result);
      if (result.rewardKind === 'liquid_usdt') {
        void qc.invalidateQueries({ queryKey: ['staking'] });
      } else {
        void qc.invalidateQueries({ queryKey: ['incptTransfers'] });
        void qc.invalidateQueries({ queryKey: ['presale'] });
      }
    } catch (e) {
      notify('error');
      setError(promocodeErrorMessage(String(e), t, t('promo.errGeneric')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-dvh bg-holo">
      <ScreenBackdrop full />
      {done && <Confetti />}

      <div className="relative z-10 flex min-h-dvh flex-col px-4 pb-[max(2.5rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{t('promo.title')}</h1>
        </header>

        {done ? (
          <div className="mt-8 flex flex-col items-center rounded-[24px] bg-white p-7 text-center shadow-card">
            {usdt ? (
              <UsdtIcon size={80} />
            ) : (
              <img src={coin} alt="" className="h-20 w-20 object-contain" />
            )}
            <div className="mt-4 text-[26px] font-semibold tracking-[-0.5px] text-ink">
              +{Number(done.amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}{' '}
              {usdt ? 'USDT' : 'INCPT'}
            </div>
            <p className="mt-1.5 text-[13px] leading-[1.5] text-muted">
              {t(usdt ? 'promo.successHintLiquid' : 'promo.successHint', {
                balance: Number(done.balance).toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                }),
              })}
            </p>
            <button
              type="button"
              onClick={onBack}
              className="mt-6 h-12 w-full rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90"
            >
              {t('common.done')}
            </button>
          </div>
        ) : (
          <>
            <p className="mt-2 text-[14px] leading-[1.55] text-muted">{t('promo.subtitle')}</p>

            <div className="mt-6 rounded-[20px] bg-white p-4 shadow-card">
              <input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submit();
                }}
                placeholder={t('promo.placeholder')}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                aria-label={t('promo.title')}
                className="w-full bg-transparent text-center font-mono text-[20px] font-medium tracking-[2px] text-ink outline-none placeholder:tracking-normal placeholder:text-black/25"
              />
            </div>

            {error && (
              <p className="mt-3 text-center text-[13px] font-semibold text-negative">{error}</p>
            )}

            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => void submit()}
              className="mt-5 h-12 w-full rounded-[15px] bg-ink text-[15px] font-semibold text-white transition-opacity active:opacity-90 disabled:opacity-40"
            >
              {busy ? t('common.loading') : t('promo.activate')}
            </button>

            <p className="mt-4 text-center text-[12px] leading-[1.5] text-muted">
              {t('promo.note')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
